/**
 * Integrations catalog — static metadata for the tools Ideoz can connect to,
 * plus the permission areas each exposes. Connection is real (OAuth via the
 * broker); the per-area permissions are a UI mockup for now — the grant/save is
 * deferred, so nothing here is persisted to the backend yet.
 */

import {
  FileText,
  HardDrive,
  Mail,
  FileSpreadsheet,
  Presentation,
  MessageSquare,
  BarChart3,
  Kanban,
  type LucideIcon,
} from 'lucide-react';

/** Access level a permission area can be granted. */
export type PermLevel = 'no_access' | 'read' | 'write' | 'read_write';

export const PERM_LABEL: Record<PermLevel, string> = {
  no_access: 'No access',
  read: 'Read',
  write: 'Write',
  read_write: 'Read & write',
};

/** One toggleable capability within an integration (e.g. Notion → Pages). */
export interface PermissionArea {
  id: string;
  title: string;
  description: string;
  /** Allowed levels, in display order. */
  levels: PermLevel[];
  /** Level selected when the tool is first opened. */
  defaultLevel: PermLevel;
}

export interface Integration {
  /** Stable id === backend `provider` (e.g. "notion"). */
  id: string;
  name: string;
  category: string;
  icon: LucideIcon;
  /** One-liner on the list card. */
  summary: string;
  /** Longer description on the detail page. */
  description: string;
  /** Primary button label when not yet connected. */
  connectLabel: string;
  /** Permission areas shown on the detail page (mockup). */
  areas: PermissionArea[];
}

const rw: PermLevel[] = ['no_access', 'read', 'write', 'read_write'];
const rwWrite: PermLevel[] = ['no_access', 'read', 'write'];

export const INTEGRATIONS: Integration[] = [
  {
    id: 'notion',
    name: 'Notion',
    category: 'Docs & wikis',
    icon: FileText,
    summary: 'Pull product docs into scope and push artifacts back.',
    description:
      'Let Ideoz read your product wiki for context and publish generated artifacts straight into a Notion database.',
    connectLabel: 'Connect Notion',
    areas: [
      {
        id: 'pages',
        title: 'Pages',
        description: 'Read page content for context, or publish artifacts as new pages.',
        levels: rw,
        defaultLevel: 'read_write',
      },
      {
        id: 'databases',
        title: 'Databases',
        description: 'Query databases and append rows for generated artifacts.',
        levels: rw,
        defaultLevel: 'read',
      },
      {
        id: 'comments',
        title: 'Comments',
        description: 'Leave review comments on pages Ideoz updated.',
        levels: rwWrite,
        defaultLevel: 'no_access',
      },
    ],
  },

  {
    id: 'jira',
    name: 'Jira',
    category: 'Issue tracking',
    icon: Kanban,
    summary: 'Pull issues and projects into scope and push generated work items back.',
    description:
      'Let Ideoz read your Jira projects and issues for context, and create or update issues from generated artifacts.',
    connectLabel: 'Connect Jira',
    areas: [
      {
        id: 'issues',
        title: 'Issues',
        description: 'Read issue details for context, or create and update issues.',
        levels: rw,
        defaultLevel: 'read_write',
      },
      {
        id: 'projects',
        title: 'Projects',
        description: 'View project metadata, boards, and workflows.',
        levels: rw,
        defaultLevel: 'read',
      },
      {
        id: 'comments',
        title: 'Comments',
        description: 'Leave comments on issues Ideoz created or updated.',
        levels: rwWrite,
        defaultLevel: 'no_access',
      },
    ],
  },
];


export const getIntegration = (id: string | undefined): Integration | undefined =>
  INTEGRATIONS.find((i) => i.id === id);

/** Default permission map (area id → level) for an integration. */
export const defaultPermissions = (integration: Integration): Record<string, PermLevel> =>
  Object.fromEntries(integration.areas.map((a) => [a.id, a.defaultLevel]));

/** Count of areas granted anything beyond "No access". */
export const enabledAreaCount = (
  integration: Integration,
  permissions: Record<string, PermLevel>
): number =>
  integration.areas.filter((a) => (permissions[a.id] ?? a.defaultLevel) !== 'no_access').length;